import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useCachedFlag } from '@openx/utils/hooks/useCachedFlag';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AMAZON_MARKETPLACE_DONT_SHOW_FLAG } from '../../../DealForm/constants';

type ShowAmazonMarketplaceConfirmationParams = {
  onConfirmed: () => void;
  onCancel: () => void;
};

export const useAmazonSyncMarketplaceConfirmation = () => {
  const { t } = useTranslation();
  const { open, close } = useConfirmationDialog();
  const [dontShowAgain, setDontShowAgain] = useCachedFlag(AMAZON_MARKETPLACE_DONT_SHOW_FLAG, false);

  const getActionsLeftContent = useCallback(
    () => (
      <FormControlLabel
        control={
          <Checkbox
            defaultChecked={false}
            onChange={e => setDontShowAgain(e.target.checked)}
            data-test="amazon-marketplace-dont-show-checkbox"
          />
        }
        label={t("Don't Show this message again.")}
        sx={{ m: 0 }}
      />
    ),
    [t, setDontShowAgain]
  );

  const showAmazonMarketplaceConfirmation = useCallback(
    ({ onConfirmed, onCancel }: ShowAmazonMarketplaceConfirmationParams) => {
      if (dontShowAgain) {
        onConfirmed();
        return;
      }

      open({
        title: t('Enrolling in Amazon Marketplace'),
        content: (
          <DialogContent dividers>
            <Typography variant="body1">
              {t('Enabling this option means Seat IDs will not be applied to this deal.')}
            </Typography>
            <Typography variant="body1">{t('Any previously entered Seat IDs will be ignored.')}</Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 4,
              }}
            >
              {t('Do you want to proceed?')}
            </Typography>
          </DialogContent>
        ),
        customContent: true,
        confirmationButtonColor: 'primary',
        confirmationLabel: t('Proceed'),
        showExtraBtn: false,
        actionsLeftContent: getActionsLeftContent(),
        onCancel: () => {
          close();
          onCancel();
        },
        onConfirmed: () => {
          close();
          onConfirmed();
        },
      });
    },
    [dontShowAgain, open, close, getActionsLeftContent, t]
  );

  return { showAmazonMarketplaceConfirmation };
};
